import React from 'react';

import {saveForUserUnencrypted} from './save-for-user-unencrypted';
import {saveForUserEncrypted} from './save-for-user-encrypted';
import {clearKey} from './clear-key';
import {clearForUser} from './clear-for-user';
import {getForUserUnencrypted} from './get-for-user-unencrypted';
import {getForUserEncrypted} from './get-for-user-encrypted';

type Config = {
    /**
     * Bypass the encryption that is usually used in local storage when saving a key for a user
     */
    skipEncryption: boolean;
};

/**
 * A hook for reading and writing a user-scoped value in local storage.
 *
 * @param key - The local storage key to use
 * @param config - Optional configuration
 * @param config.skipEncryption - Whether to skip encryption (defaults to false)
 * @returns A tuple of [value, setValue, clearValue]
 *
 * @example
 * const [theme, setTheme, clearTheme] = useLocalStorageValueForUser<string>('THEME');
 */
export const useLocalStorageValueForUser = <T>(key: string, config?: Config) => {
    const skipEncryption = config?.skipEncryption ?? false;
    const withKeyPart = false;
    const customLocalStorageEvent = `localStorageValueChangedFor${key}`;

    const [value, _setValue] = React.useState<T | null>(() => {
        return getLocalStorageValue(key, skipEncryption);
    });

    const setValue = React.useCallback(
        (value: T) => {
            if (skipEncryption) {
                saveForUserUnencrypted(key, value);
            } else {
                saveForUserEncrypted(key, value, withKeyPart);
            }
            _setValue(value);
            window.dispatchEvent(new Event(customLocalStorageEvent));
        },
        [skipEncryption, customLocalStorageEvent, key, withKeyPart],
    );

    const clearValue = React.useCallback(() => {
        if (skipEncryption) {
            clearKey(key);
        } else {
            clearForUser(key);
        }
        _setValue(null);
        window.dispatchEvent(new Event(customLocalStorageEvent));
    }, [customLocalStorageEvent, key, skipEncryption]);

    React.useEffect(() => {
        const onLocalStorageDidChange = () => {
            const currentLocalStorageValue = getLocalStorageValue<T>(key, skipEncryption);
            if (currentLocalStorageValue !== value) {
                _setValue(currentLocalStorageValue);
            }
        };
        window.addEventListener(customLocalStorageEvent, onLocalStorageDidChange);
        return () => window.removeEventListener(customLocalStorageEvent, onLocalStorageDidChange);
    }, [customLocalStorageEvent, key, setValue, value, withKeyPart, skipEncryption]);

    return [value, setValue, clearValue] as const;
};

const getLocalStorageValue = <T>(key: string, skipEncryption: boolean): T | null => {
    const withKeyPart = false;
    return skipEncryption ? getForUserUnencrypted(key) : getForUserEncrypted(key, withKeyPart);
};
